/**
 * EnrichmentFilterBar — a per-contact filter control scoped to the Import
 * Detail `ContactsTable` (subtask 04). Distinct from the Imports LIST's
 * import-level "Enriched" tag chip: this operates at contact granularity within
 * one import. Currently a single toggle — "All" vs. "Enrichment failed" — so a
 * recruiter can isolate the contacts whose enrichment failed. Follows the
 * `ImportsToolbar.tsx` filter-chip visual pattern (design-system tokens only).
 */

/** The contact-level enrichment filter this page's table can apply. */
export type EnrichmentFilter = "all" | "failed";

const CHIPS: readonly { label: string; value: EnrichmentFilter }[] = [
  { label: "All", value: "all" },
  { label: "Enrichment failed", value: "failed" },
];

export function EnrichmentFilterBar({
  value,
  onChange,
  failedCount,
}: {
  value: EnrichmentFilter;
  onChange: (value: EnrichmentFilter) => void;
  /** Number of failed-enrichment contacts in the WHOLE import (from the
   *  contacts response's `counts`, under the other active filters). */
  failedCount: number;
}) {
  return (
    <fieldset
      className="flex min-w-0 flex-wrap items-center gap-1.5 border-0 p-0 m-0"
      data-testid="enrichment-filter"
    >
      <legend className="sr-only">Filter contacts by enrichment outcome</legend>
      {CHIPS.map((chip) => {
        const active = chip.value === value;
        const label = chip.value === "failed" ? `${chip.label} (${failedCount})` : chip.label;
        return (
          <button
            key={chip.value}
            type="button"
            aria-pressed={active}
            data-chip={chip.value}
            onClick={() => onChange(chip.value)}
            className={
              active
                ? "rounded-full border border-accent bg-accent-muted px-3 py-1 text-label text-accent"
                : "rounded-full border border-border bg-surface px-3 py-1 text-label text-text-secondary hover:border-border-hover"
            }
          >
            {label}
          </button>
        );
      })}
    </fieldset>
  );
}
